import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import { ClipboardCheck, NotebookText } from "lucide-react";
import "./Giornata.css";
import { useAuthContext } from "../../hooks/useAuthContext";
import { useCollection } from "../../hooks/useCollection";
import { useDocument } from "../../hooks/useDocument";
import { database, timestamp } from "../../firebase/config";

const TASK_STATUS_OPEN = "open";
const TASK_STATUS_DONE = "done";
const MISSING_DOCUMENT_ERROR = "Il documento selezionato non esiste";
const PAUSE_OPTIONS = [
  { value: "", label: "Seleziona la pausa" },
  { value: "nessuno", label: "Nessuna pausa" },
  { value: "0.5", label: "30 minuti" },
  { value: "1", label: "1 ora" },
  { value: "1.5", label: "1 ora e 30" },
  { value: "2", label: "2 ore" },
];

const createTaskObject = (descrizione = "", status = TASK_STATUS_OPEN) => ({
  descrizione: String(descrizione || "").trim(),
  status: status === TASK_STATUS_DONE ? TASK_STATUS_DONE : TASK_STATUS_OPEN,
});

const normalizeActivityTasks = (value) => {
  if (Array.isArray(value)) {
    return value
      .map((task) => {
        if (typeof task === "string") {
          return createTaskObject(task, TASK_STATUS_OPEN);
        }

        if (task && typeof task === "object") {
          const descrizione = String(
            task.descrizione || task.label || task.title || task.text || "",
          ).trim();
          if (!descrizione) return null;
          return createTaskObject(descrizione, task.status);
        }

        return null;
      })
      .filter(Boolean);
  }

  if (typeof value === "string") {
    return value
      .split("\n")
      .map((task) => createTaskObject(task, TASK_STATUS_OPEN))
      .filter((task) => task.descrizione);
  }

  return [];
};

const parseDateParam = (value) => {
  const normalized = String(value || "").trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(normalized)) return null;

  const [year, month, day] = normalized.split("-").map(Number);
  const parsedDate = new Date(year, month - 1, day);
  if (Number.isNaN(parsedDate.getTime())) return null;

  return format(parsedDate, "yyyy-MM-dd") === normalized ? parsedDate : null;
};

const capitalize = (value) =>
  value ? value.charAt(0).toUpperCase() + value.slice(1) : "";

const getDateFromValue = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value?.toDate === "function") {
    try {
      return value.toDate();
    } catch {
      return null;
    }
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatDateKey = (value) => {
  const date = getDateFromValue(value);
  return date ? format(date, "yyyy-MM-dd") : "";
};

const formatTimeValue = (value) => {
  const date = getDateFromValue(value);
  return date ? format(date, "HH:mm") : "--:--";
};

export default function Giornata() {
  const { user } = useAuthContext();
  const { date } = useParams();
  const [taskUpdateError, setTaskUpdateError] = useState("");
  const [isUpdatingTasks, setIsUpdatingTasks] = useState(false);
  const [presenceSaveError, setPresenceSaveError] = useState("");
  const [presenceSaveSuccess, setPresenceSaveSuccess] = useState("");
  const [isSavingPresence, setIsSavingPresence] = useState(false);
  const [showPauseModal, setShowPauseModal] = useState(false);
  const [selectedPauseDuration, setSelectedPauseDuration] = useState("");

  const selectedDate = useMemo(() => parseDateParam(date), [date]);
  const selectedDateKey = useMemo(
    () => (selectedDate ? format(selectedDate, "yyyy-MM-dd") : ""),
    [selectedDate],
  );
  const documentId = user?.uid && selectedDate ? `${user.uid}_${date}` : null;
  const timbratureQrQuery = useMemo(
    () => (user?.uid ? ["userId", "==", user.uid] : null),
    [user?.uid],
  );
  const presenzeQuery = useMemo(
    () => (user?.uid ? ["userId", "==", user.uid] : null),
    [user?.uid],
  );

  const { document: activity, error } = useDocument("attivita", documentId);
  const { documents: timbratureQrData, error: timbratureQrError } =
    useCollection(
      timbratureQrQuery ? "timbrature_qr" : null,
      timbratureQrQuery,
    );
  const { documents: presenzeData, error: presenzeError } = useCollection(
    presenzeQuery ? "presenze" : null,
    presenzeQuery,
  );

  const dayLabel = useMemo(() => {
    if (!selectedDate) return "Data non valida";
    return capitalize(format(selectedDate, "EEEE d MMMM yyyy", { locale: it }));
  }, [selectedDate]);

  const tasks = useMemo(
    () => normalizeActivityTasks(activity?.tasks),
    [activity?.tasks],
  );

  useEffect(() => {
    setTaskUpdateError("");
  }, [activity?.id, activity?.tasks]);

  useEffect(() => {
    setPresenceSaveError("");
    setPresenceSaveSuccess("");
    setShowPauseModal(false);
    setSelectedPauseDuration("");
  }, [selectedDateKey, activity?.id]);

  const completedTasksCount = useMemo(
    () => tasks.filter((task) => task.status === TASK_STATUS_DONE).length,
    [tasks],
  );

  const selectedTimbratura = useMemo(() => {
    const matches = (timbratureQrData || []).filter((timbratura) => {
      const businessDate = String(timbratura?.businessDate || "").trim();
      return (
        businessDate === selectedDateKey ||
        formatDateKey(timbratura?.openedAt) === selectedDateKey
      );
    });

    if (!matches.length) return null;

    return [...matches].sort((first, second) => {
      const secondDate =
        getDateFromValue(second?.openedAt)?.getTime() ||
        getDateFromValue(second?.createdAt)?.getTime() ||
        0;
      const firstDate =
        getDateFromValue(first?.openedAt)?.getTime() ||
        getDateFromValue(first?.createdAt)?.getTime() ||
        0;
      return secondDate - firstDate;
    })[0];
  }, [selectedDateKey, timbratureQrData]);

  const selectedDayPresenza = useMemo(() => {
    const matches = (presenzeData || []).filter(
      (presenza) =>
        formatDateKey(presenza?.dataRegistrazione) === selectedDateKey,
    );

    if (!matches.length) return null;

    if (selectedTimbratura?.id) {
      const linkedPresenza = matches.find(
        (presenza) => presenza?.timbraturaQrId === selectedTimbratura.id,
      );
      if (linkedPresenza) return linkedPresenza;
    }

    return matches[0];
  }, [presenzeData, selectedDateKey, selectedTimbratura?.id]);

  const hasLinkedPresenza = Boolean(
    selectedDayPresenza?.id &&
    selectedTimbratura?.id &&
    selectedDayPresenza?.timbraturaQrId === selectedTimbratura.id,
  );

  const canCreatePresence = Boolean(
    activity &&
    activity.status !== "assenza" &&
    selectedTimbratura?.openedAt &&
    selectedTimbratura?.closedAt &&
    !selectedDayPresenza &&
    !isSavingPresence,
  );

  const handleToggleTask = useCallback(
    async (taskIndex) => {
      if (!documentId || !activity || isUpdatingTasks) return;

      const nextTasks = tasks.map((task, index) =>
        index === taskIndex
          ? createTaskObject(
              task.descrizione,
              task.status === TASK_STATUS_DONE
                ? TASK_STATUS_OPEN
                : TASK_STATUS_DONE,
            )
          : createTaskObject(task.descrizione, task.status),
      );

      setIsUpdatingTasks(true);
      setTaskUpdateError("");

      try {
        await database
          .collection("attivita")
          .doc(documentId)
          .set(
            {
              tasks: nextTasks,
              updatedAt: timestamp.fromDate(new Date()),
              updatedBy: user?.uid || "",
            },
            { merge: true },
          );
      } catch (toggleError) {
        setTaskUpdateError(
          toggleError?.message ||
            "Impossibile aggiornare il task in questo momento.",
        );
      } finally {
        setIsUpdatingTasks(false);
      }
    },
    [activity, documentId, isUpdatingTasks, tasks, user?.uid],
  );

  const handleOpenPauseModal = useCallback(() => {
    if (!canCreatePresence) return;
    setPresenceSaveError("");
    setPresenceSaveSuccess("");
    setSelectedPauseDuration("");
    setShowPauseModal(true);
  }, [canCreatePresence]);

  const handleClosePauseModal = useCallback(() => {
    if (isSavingPresence) return;
    setShowPauseModal(false);
    setSelectedPauseDuration("");
  }, [isSavingPresence]);

  const handleSavePresence = useCallback(async () => {
    if (
      !canCreatePresence ||
      !selectedTimbratura ||
      !user?.uid ||
      !selectedPauseDuration
    ) {
      return;
    }

    setIsSavingPresence(true);
    setPresenceSaveError("");
    setPresenceSaveSuccess("");

    const now = timestamp.fromDate(new Date());
    const cantiere =
      activity?.project && typeof activity.project === "object"
        ? {
            nome: activity.project.nome || "",
            codice: activity.project.codice || null,
          }
        : null;
    const mezzo =
      activity?.mezzo && typeof activity.mezzo === "object"
        ? {
            id: activity.mezzo.id || null,
            modello: activity.mezzo.modello || "",
            targa: activity.mezzo.targa || "",
          }
        : null;

    try {
      const created = await database.collection("presenze").add({
        userId: user.uid,
        userEmail: user.email || "",
        dataRegistrazione: new Date(`${selectedDateKey}T00:00:00`),
        tipoPresenza: "lavorativa",
        oraIngresso: getDateFromValue(selectedTimbratura.openedAt),
        oraUscita: getDateFromValue(selectedTimbratura.closedAt),
        cantiere,
        mezzo,
        durataPausa:
          selectedPauseDuration === "nessuno"
            ? "nessuno"
            : parseFloat(selectedPauseDuration),
        isFerie: false,
        isMalattia: false,
        isRiposo: false,
        codiceMalattia: null,
        note: String(activity?.note || "").trim(),
        hasTimbratura: true,
        timbraturaId: null,
        timbraturaQrId: selectedTimbratura.id,
        createdAt: now,
        updatedAt: now,
      });

      if (created?.id) {
        try {
          await database
            .collection("timbrature_qr")
            .doc(selectedTimbratura.id)
            .set(
              {
                presenzaId: created.id,
                updatedAt: now,
              },
              { merge: true },
            );
        } catch {
          setShowPauseModal(false);
          setSelectedPauseDuration("");
          setPresenceSaveSuccess(
            "Presenza salvata. Il collegamento con la timbratura verra aggiornato in un secondo momento.",
          );
          return;
        }
      }

      setShowPauseModal(false);
      setSelectedPauseDuration("");
      setPresenceSaveSuccess("Presenza salvata correttamente.");
    } catch (saveError) {
      setPresenceSaveError(
        saveError?.message ||
          "Impossibile salvare la presenza per questa giornata.",
      );
    } finally {
      setIsSavingPresence(false);
    }
  }, [
    activity,
    canCreatePresence,
    selectedPauseDuration,
    selectedDateKey,
    selectedTimbratura,
    user?.email,
    user?.uid,
  ]);

  const planningStatusLabel = activity
    ? activity.status === "assenza"
      ? "Assenza"
      : "Pianificazione presente"
    : "Nessuna pianificazione";

  const isMissingActivity = !activity && error === MISSING_DOCUMENT_ERROR;

  return (
    <div className="giornata-page">
      <div className="giornata-hero">
        <div>
          <div className="giornata-kicker">Giornata selezionata</div>
          <h1>{dayLabel}</h1>
        </div>
        <div
          className={`giornata-status-pill ${
            activity?.status === "assenza"
              ? "assenza"
              : activity
                ? "planned"
                : "empty"
          }`}
        >
          {planningStatusLabel}
        </div>
      </div>

      {!selectedDate ? (
        <div className="giornata-empty-card error">
          La data selezionata non e valida.
        </div>
      ) : error && error !== MISSING_DOCUMENT_ERROR ? (
        <div className="giornata-empty-card error">
          Impossibile caricare i dati della giornata.
        </div>
      ) : !activity && !error ? (
        <div className="giornata-empty-card">Caricamento giornata...</div>
      ) : isMissingActivity ? (
        <div className="giornata-empty-card">
          Per questa giornata non risulta ancora alcuna pianificazione.
        </div>
      ) : (
        <>
          <div className="giornata-summary-grid">
            <div className="giornata-summary-card">
              <span>Cantiere</span>
              <strong>
                {activity?.status === "assenza"
                  ? "Non previsto"
                  : activity?.project?.nome || "Non assegnato"}
              </strong>
            </div>
            <div className="giornata-summary-card">
              <span>Mezzo</span>
              <strong>
                {activity?.status === "assenza"
                  ? "Non previsto"
                  : activity?.mezzo?.targa ||
                    activity?.mezzo?.modello ||
                    "Non assegnato"}
              </strong>
            </div>
            <div className="giornata-summary-card">
              <span>Task</span>
              <strong>
                {completedTasksCount}/{tasks.length}
              </strong>
            </div>
          </div>

          {activity?.note && (
            <section className="giornata-panel giornata-note-panel">
              <div className="giornata-note-title">
                <NotebookText size={16} />
                Note
              </div>
              <p>{activity.note}</p>
            </section>
          )}

          <section className="giornata-panel">
            <div className="giornata-panel-head">
              <ClipboardCheck size={18} />
              <div>
                <h2>Task</h2>
                <p>Flagga i task completati durante la giornata.</p>
              </div>
            </div>

            {taskUpdateError && (
              <div className="giornata-empty-inline error">
                {taskUpdateError}
              </div>
            )}

            {tasks.length > 0 ? (
              <div className="giornata-task-list">
                {tasks.map((task, index) => {
                  const isDone = task.status === TASK_STATUS_DONE;

                  return (
                    <label
                      key={`giornata-task-${index}`}
                      className={`giornata-task ${isDone ? "done" : ""}`}
                    >
                      <div className="giornata-task-checkbox">
                        <input
                          type="checkbox"
                          checked={isDone}
                          onChange={() => handleToggleTask(index)}
                          disabled={isUpdatingTasks}
                        />
                        <span className="giornata-task-checkmark" />
                      </div>
                      <div className="giornata-task-content">
                        <div className="giornata-task-top">
                          <span className="giornata-task-index">
                            Task {index + 1}
                          </span>
                          <span
                            className={`giornata-task-status ${
                              isDone ? "done" : "open"
                            }`}
                          >
                            {isDone ? "Completato" : "Aperto"}
                          </span>
                        </div>
                        <p>{task.descrizione}</p>
                      </div>
                    </label>
                  );
                })}
              </div>
            ) : (
              <div className="giornata-empty-inline">
                Nessun task associato a questa giornata.
              </div>
            )}
          </section>

          <section className="giornata-panel">
            <div className="giornata-panel-head">
              <ClipboardCheck size={18} />
              <div>
                <h2>Timbratura</h2>
              </div>
            </div>

            {timbratureQrError && (
              <div className="giornata-empty-inline error">
                Impossibile caricare la timbratura QR del giorno.
              </div>
            )}

            {presenzeError && (
              <div className="giornata-empty-inline error">
                Impossibile verificare la presenza collegata.
              </div>
            )}

            {presenceSaveError && (
              <div className="giornata-empty-inline error">
                {presenceSaveError}
              </div>
            )}

            {presenceSaveSuccess && (
              <div className="giornata-empty-inline success">
                {presenceSaveSuccess}
              </div>
            )}

            {selectedTimbratura ? (
              <>
                <div className="giornata-presenza-grid">
                  <div className="giornata-presenza-card">
                    <span>Ora ingresso</span>
                    <strong>
                      {formatTimeValue(selectedTimbratura.openedAt)}
                    </strong>
                  </div>
                  <div className="giornata-presenza-card">
                    <span>Ora uscita</span>
                    <strong>
                      {formatTimeValue(selectedTimbratura.closedAt)}
                    </strong>
                  </div>
                </div>

                <div
                  className={`giornata-presenza-state ${
                    hasLinkedPresenza
                      ? "linked"
                      : selectedDayPresenza
                        ? "existing"
                        : "missing"
                  }`}
                >
                  <strong>
                    {hasLinkedPresenza
                      ? "Presenza collegata"
                      : selectedDayPresenza
                        ? "Presenza già presente"
                        : "Presenza non ancora salvata"}
                  </strong>
                </div>

                {!selectedDayPresenza && (
                  <div className="giornata-presenza-actions">
                    <button
                      type="button"
                      className="giornata-primary-btn"
                      onClick={handleOpenPauseModal}
                      disabled={!canCreatePresence}
                    >
                      {isSavingPresence ? "Salvataggio..." : "Salva presenza"}
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="giornata-empty-inline">
                Nessuna timbratura QR trovata per la giornata selezionata.
              </div>
            )}
          </section>
        </>
      )}

      {showPauseModal && (
        <div
          className="giornata-modal-overlay"
          role="presentation"
          onClick={handleClosePauseModal}
        >
          <div
            className="giornata-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="giornata-pausa-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="giornata-modal-header">
              <h3 id="giornata-pausa-title">Definisci la pausa</h3>
              <p>
                Seleziona la durata della pausa prima di salvare la presenza del
                giorno.
              </p>
            </div>

            <div className="giornata-modal-body">
              {presenceSaveError && (
                <div className="giornata-empty-inline error">
                  {presenceSaveError}
                </div>
              )}

              <label className="giornata-field">
                <span>Durata pausa</span>
                <select
                  value={selectedPauseDuration}
                  onChange={(event) =>
                    setSelectedPauseDuration(event.target.value)
                  }
                  disabled={isSavingPresence}
                >
                  {PAUSE_OPTIONS.map((option) => (
                    <option
                      key={option.value || "placeholder"}
                      value={option.value}
                    >
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <div className="giornata-modal-actions">
              <button
                type="button"
                className="giornata-secondary-btn"
                onClick={handleClosePauseModal}
                disabled={isSavingPresence}
              >
                Annulla
              </button>
              <button
                type="button"
                className="giornata-primary-btn"
                onClick={handleSavePresence}
                disabled={!selectedPauseDuration || isSavingPresence}
              >
                {isSavingPresence ? "Salvataggio..." : "Conferma e salva"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
